// Regla operativa por placa: GPIN general, GPIN VIP, GPOUT VIP, GPOUT general.
// No se deduplican eventos distintos ni se completan movimientos ausentes.
export function buildVipCycles(events) {
  const active = new Map();
  const cycles = [];
  const ordered = [...events].sort(
    (a, b) =>
      a.ms - b.ms || String(a.eventId).localeCompare(String(b.eventId), "en", { numeric: true })
  );
  for (const event of ordered) {
    const plate = String(event.plate || "")
      .trim()
      .toUpperCase();
    let cycle = plate ? active.get(plate) : null;
    if (event.eventType === "GPIN") {
      if (!cycle) {
        cycle = {
          ...event,
          plate,
          generalEntry: event,
          vipEntry: null,
          vipExit: null,
          generalExit: null,
          movements: [event],
          ambiguous: !plate,
        };
        cycles.push(cycle);
        if (plate) active.set(plate, cycle);
      } else {
        cycle.movements.push(event);
        if (!cycle.vipEntry && !cycle.vipExit && event.ms > cycle.generalEntry.ms)
          cycle.vipEntry = event;
        else cycle.ambiguous = true;
      }
    } else if (event.eventType === "GPOUT") {
      if (!cycle) {
        cycles.push({
          ...event,
          plate,
          generalEntry: null,
          vipEntry: null,
          vipExit: null,
          generalExit: null,
          unmatchedExit: event,
          movements: [event],
          ambiguous: false,
        });
      } else {
        cycle.movements.push(event);
        if (cycle.vipEntry && !cycle.vipExit) {
          cycle.vipExit = event;
          if (event.ms <= cycle.vipEntry.ms) cycle.ambiguous = true;
        } else {
          cycle.generalExit = event;
          if (cycle.vipExit && event.ms <= cycle.vipExit.ms) cycle.ambiguous = true;
          active.delete(plate);
        }
      }
    }
  }
  const elapsed = (from, to) => (from && to && to.ms >= from.ms ? (to.ms - from.ms) / 60000 : null);
  return cycles
    .map((cycle) => {
      const { generalEntry, vipEntry, vipExit, generalExit, unmatchedExit, ambiguous } = cycle;
      const isVipVisit = Boolean(vipEntry && !ambiguous);
      return {
        ...cycle,
        id: cycle.movements.map((event) => event.eventId).join(":"),
        eventIds: cycle.movements.map((event) => event.eventId).join(", "),
        entryEventId: generalEntry?.eventId || null,
        vipEntryEventId: vipEntry?.eventId || null,
        vipExitEventId: vipExit?.eventId || null,
        exitEventId: generalExit?.eventId || unmatchedExit?.eventId || null,
        entryDate: generalEntry?.date || null,
        entryTime: generalEntry?.time || null,
        vipEntryAt: vipEntry ? `${vipEntry.date}T${vipEntry.time}` : null,
        vipExitAt: vipExit ? `${vipExit.date}T${vipExit.time}` : null,
        vipEntryDate: vipEntry?.date || null,
        vipEntryTime: vipEntry?.time || null,
        vipExitDate: vipExit?.date || null,
        vipExitTime: vipExit?.time || null,
        exitDate: generalExit?.date || unmatchedExit?.date || null,
        exitTime: generalExit?.time || unmatchedExit?.time || null,
        isVipVisit,
        vipMinutes: isVipVisit ? elapsed(vipEntry, vipExit) : null,
        totalMinutes: ambiguous ? null : elapsed(generalEntry, generalExit),
        pairingStatus: ambiguous
          ? "Secuencia por revisar"
          : unmatchedExit
          ? "Sin entrada"
          : !vipEntry
          ? generalExit
            ? "Solo general"
            : "Sin ingreso VIP"
          : !vipExit
          ? "Sin salida VIP"
          : !generalExit
          ? "Sin salida general"
          : "Ciclo VIP completo",
      };
    })
    .sort((a, b) => b.ms - a.ms || a.plate.localeCompare(b.plate));
}

export function summarizeVipCycles(cycles) {
  const plates = new Map();
  let total = 0,
    completed = 0,
    totalMinutes = 0;
  for (const cycle of cycles) {
    if (!cycle.isVipVisit) continue;
    total++;
    if (!plates.has(cycle.plate))
      plates.set(cycle.plate, { plate: cycle.plate, count: 0, completed: 0, minutes: 0 });
    const row = plates.get(cycle.plate);
    row.count++;
    if (cycle.vipMinutes != null) {
      row.completed++;
      row.minutes += cycle.vipMinutes;
      completed++;
      totalMinutes += cycle.vipMinutes;
    }
  }
  return {
    total,
    uniquePlates: plates.size,
    averageMinutes: completed ? totalMinutes / completed : null,
    completed,
    excluded: cycles.filter((cycle) => !cycle.isVipVisit).length,
    plates: [...plates.values()]
      .sort((a, b) => b.count - a.count || a.plate.localeCompare(b.plate))
      .map((row) => ({
        ...row,
        averageMinutes: row.completed ? row.minutes / row.completed : null,
      })),
  };
}
